import React, { useState, useEffect, useRef } from 'react';
import { getQueryParam } from '../utils/navigation';
import axios from 'axios';
import QRCode from 'qrcode';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import PrintHeader from '../components/PrintHeader';
import PrintFooter from '../components/PrintFooter';

// Create a separate axios instance for public endpoints
const publicAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5003/api'
});

const PrintSheet = () => {
  const { user } = useAuth();
  const sheetId = getQueryParam('SHEETID');
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const canvasRef = useRef(null);
  const circleRef1 = useRef(null);
  const circleRef2 = useRef(null);
  const qrCanvasRef = useRef(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [qrCodeGenerated, setQrCodeGenerated] = useState(false);

  useEffect(() => {
    if (sheetId) {
      fetchPrintData();
      fetchQRCodeUrl();
    } else {
      setError('رقم الاستمارة غير موجود');
      setLoading(false);
    }
  }, [sheetId]);

  useEffect(() => {
    if (!loading && userData && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.font = '16px Arial';
      ctx.textAlign = 'left';
      ctx.fillText('4*6', 40, 50);
    }

    if (circleRef1.current) {
      const c = circleRef1.current;
      const ctx = c.getContext('2d');
      ctx.beginPath();
      ctx.arc(50, 50, 45, 0, 2 * Math.PI);
      ctx.stroke();
    }

    if (circleRef2.current) {
      const c = circleRef2.current;
      const ctx = c.getContext('2d');
      ctx.beginPath();
      ctx.arc(50, 50, 45, 0, 2 * Math.PI);
      ctx.stroke();
    }
  }, [loading, userData]);

  useEffect(() => {
    if (!loading && qrCodeUrl && qrCanvasRef.current && !qrCodeGenerated) {
      setTimeout(() => {
        console.log('Generating QR code for URL:', qrCodeUrl);
        QRCode.toCanvas(qrCanvasRef.current, qrCodeUrl, {
          width: 110,
          height: 110,
          margin: 3,
          errorCorrectionLevel: 'H',
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        }, (error) => {
          if (error) {
            console.error('QR Code generation error:', error);
            setTimeout(() => {
              if (qrCanvasRef.current) {
                QRCode.toCanvas(qrCanvasRef.current, qrCodeUrl, {
                  width: 110,
                  height: 110,
                  margin: 3,
                  errorCorrectionLevel: 'H'
                }, (retryError) => {
                  if (retryError) {
                    console.error('QR Code retry failed:', retryError);
                  } else {
                    setQrCodeGenerated(true);
                  }
                });
              }
            }, 500);
          } else {
            setQrCodeGenerated(true);
          }
        });
      }, 100);
    }
  }, [loading, qrCodeUrl, qrCodeGenerated]);

  const fetchPrintData = async () => {
    try {
      const response = await publicAxios.get(`/public/print/${sheetId}`);
      if (response.data.success) {
        setUserData(response.data.data);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setError('لم يتم العثور على البيانات');
      } else {
        setError('حدث خطأ في جلب البيانات');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchQRCodeUrl = async () => {
    try {
      const response = await publicAxios.get('/public/settings/qr-code-url');
      if (response.data.success && response.data.data.qrCodeUrl) {
        setQrCodeUrl(response.data.data.qrCodeUrl);
      }
    } catch (error) {
      console.error('Error fetching QR code URL:', error);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, '0')}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getFullYear()}`;
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return '-';
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const formatAddress = (address) => {
    if (!address) return '-';
    if (typeof address === 'string') return address;
    const parts = [];
    if (address.details) parts.push(address.details);
    if (address.city) parts.push(address.city);
    if (address.governorate) parts.push(address.governorate);
    return parts.join(' - ') || '-';
  };

  const handlePrint = () => {
    const removeElements = ['removeLayoutHeader', 'footer', 'printBtn', 'closeBtn', 'editBtn'];
    removeElements.forEach(id => {
      const el = document.getElementById(id);
      if (el) el.style.display = 'none';
    });

    window.scrollTo(0, 0);
    window.print();

    removeElements.forEach(id => {
      const el = document.getElementById(id);
      if (el) el.style.display = '';
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">جاري تحميل البيانات...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <div className="text-red-600 mb-4">
            <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-lg font-medium">{error}</p>
          </div>
          <button
            onClick={() => window.close()}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <>إغلاق</>
          </button>
        </div>
      </div>
    );
  }

  const prenupData = userData?.prenupData || {};

  return (
    <div>
      {/* Original styling exactly as the source */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .pic {
            display: block;
          }

          body {
            font-family: 'Cairo',sans-serif;
          }

          .qr-code {
            max-width: 100px;
            max-height: 110px;
          }

          .circle-stamp {
            width: 115px;
            height: 115px;
            display: block;
            margin: 0 auto;
          }

          p, .form-check-label {
            font-size: 18px;
          }

          h2 {
            font-weight: bold;
          }

          @media print {
            h2 {
              font-size: 18px !important;
            }

            p {
              font-size: 16px !important;
            }

            .font14 {
              font-size: 16px !important;
            }

            .chartjs-size-monitor {
              display: none !important;
            }
          }

          .Electrophoresis-item {
            flex: 0 0 18%;
          }
        `
      }} />

      {/* Show Header for logged-in users, PrintHeader for public visitors */}
      {user ? <Header /> : <PrintHeader />}

      <div>
        <header className="bg-header small-header"></header>

        <div className="container mt-5" id="TableDiv">
          <div className="row">
            <div className="pic col-3" style={{display: 'block', direction: 'ltr', marginRight: 'auto'}}>
              <canvas ref={canvasRef} id="myCanvas" width="110" height="110" style={{border:'1px solid #000000'}}>
              </canvas><br/><span>ختم شعار الجمهورية</span>
            </div>
          </div>
          <div className="row">
            <div className="col-3 font-weight-bold"><p style={{paddingTop: '40px'}}>تاريخ الإصدار : {formatDate(prenupData.createdAt)}</p></div>
            <div className="col-4 font-weight-bold"><p style={{paddingTop: '40px'}}>اسم الوحدة: {userData?.unitName || ''}</p></div>
            <div className="col-3 font-weight-bold"><p style={{paddingTop: '40px'}}>المحافظة: {userData?.governorate || ''}</p></div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <h2>البيانات الأساسية</h2>

              <div style={{backgroundColor:'white'}}>
                <div className="row">
                  <div className="col-5">
                    <p className="mr-3 font14" style={{fontFamily: 'cairo'}}>
                      الاسم : <span className="font-weight-bold">{userData?.fullName || '-'}</span>
                    </p>
                  </div>
                  <div className="col-4">
                    <p className="mr-3 font14" style={{fontFamily: 'cairo'}}>
                      {prenupData.nationalityType === 'N' ? 'رقم جواز السفر' : 'الرقم القومى'} : <span className="font-weight-bold">{userData?.nationalId || '-'}</span>
                    </p>
                  </div>
                  <div className="col-2">
                    <p className="mr-3 font14" style={{fontFamily: 'cairo'}}>
                      النوع : <span className="font-weight-bold">{userData?.gender === 'male' ? 'ذكر' : 'أنثى'}</span>
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-5">
                    <p className="mr-3 font14" style={{fontFamily: 'cairo'}}>الجنسية : <span className="font-weight-bold">{prenupData.nationality || 'مصري'}</span></p>
                  </div>
                  <div className="col-4">
                    <p className="mr-3 font14" style={{fontFamily: 'cairo'}}>
                      السن : <span className="font-weight-bold">{calculateAge(userData?.dateOfBirth)}</span>
                    </p>
                  </div>
                  <div className="col-3">
                    <p className="mr-3 font14" style={{fontFamily: 'cairo'}}>
                      رقم الهاتف : <span className="font-weight-bold">{userData?.phone || '-'}</span>
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-5">
                    <p className="mr-3 font14" style={{fontFamily: 'cairo'}}>العنوان بالبطاقة : <span className="font-weight-bold">{formatAddress(userData?.address)}</span></p>
                  </div>
                  <div className="col-6">
                    <p className="mr-3 font14" style={{fontFamily: 'cairo'}}>
                      عنوان سكن الزوجية : <span className="font-weight-bold">{prenupData.marriageAddress || ''}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <br />

            <div className="col-md-12">
              <h2>الفحوصات الطبية</h2>

              <div style={{backgroundColor:'white'}}>
                <div className="row">
                  <div className="col-4">
                    <p className="mr-3 font14" style={{fontFamily: 'cairo'}}>الطول(سم) : <span className="font-weight-bold">{prenupData.height || '-'}</span></p>
                  </div>
                  <div className="col-4">
                    <p style={{fontFamily: 'cairo'}}>
                      الوزن(كجم) : <span className="font-weight-bold">{prenupData.weight || '-'}</span>
                    </p>
                  </div>
                  <div className="col-4">
                    <p style={{fontFamily: 'cairo', display:'flex'}}>
                      <span>BMI :</span>
                      <span className="font-weight-bold">{prenupData.bmi || '-'}</span>
                    </p>
                  </div>
                </div>

                <div className="row">
                  <div className="col-4" id="RH">
                    <p className="mr-3 font14" style={{fontFamily: 'cairo'}}>
                      RH : <span className="font-weight-bold">{prenupData.rhFactor === 'Y' ? 'إيجابى' : prenupData.rhFactor === 'N' ? 'سلبى' : ''}</span>
                    </p>
                  </div>
                  <div className="col-4" id="bloodtype">
                    <p style={{fontFamily: 'cairo'}}>
                      فصيلة الدم: <span className="font-weight-bold">
                        {prenupData.bloodType || ''}
                        {prenupData.rhFactor === 'Y' ? '+' : prenupData.rhFactor === 'N' ? '-' : ''}
                      </span>
                    </p>
                  </div>
                  <div className="col-4">
                    <p style={{fontFamily: 'cairo', display:'flex'}}>
                      <span>Hb : </span>
                      <span className="font-weight-bold">{prenupData.hemoglobin || '-'}</span>
                    </p>
                  </div>
                </div>

                <div className="row">
                  <div className="col-4" id="HBSAG">
                    <p className="mr-3 font14" style={{fontFamily: 'cairo'}}>
                      HBsAg : <span className="font-weight-bold">{prenupData.hbsAg === '1' ? 'متفاعل' : prenupData.hbsAg === '0' ? 'غير متفاعل' : ''}</span>
                    </p>
                  </div>
                  <div className="col-4" id="ANTIHIV">
                    <p style={{fontFamily: 'cairo'}}>
                      Anti-HIV : <span className="font-weight-bold">{prenupData.antiHIV === '1' ? 'متفاعل' : prenupData.antiHIV === '0' ? 'غير متفاعل' : ''}</span>
                    </p>
                  </div>
                  <div className="col-4" id="ANTIHCV">
                    <p style={{fontFamily: 'cairo'}}>
                      Anti-HCV : <span className="font-weight-bold">{prenupData.antiHCV === '1' ? 'متفاعل' : prenupData.antiHCV === '0' ? 'غير متفاعل' : ''}</span>
                    </p>
                  </div>
                </div>

                <div className="row">
                  <div className="col-4">
                    <p style={{fontFamily: 'cairo'}}>
                      ضغط الدم : <span className="font-weight-bold">{prenupData.bloodPressure || '-'}</span>
                    </p>
                  </div>
                  <div className="col-4">
                    <p style={{fontFamily: 'cairo'}}>
                      نتيجة فحص السكر(العشوائى) : <span className="font-weight-bold">{prenupData.randomGlucose || '-'}</span>
                    </p>
                  </div>
                </div>

                <div className="row">
                  <p style={{direction: 'ltr', fontWeight: 'bold', textDecoration: 'underline', fontFamily: 'cairo', width: '100%', display: 'block', textAlign: 'left'}}>
                    Hb Electrophoresis :
                  </p>
                </div>
                <div className="row col-md-12" style={{direction:'ltr'}}>
                  <div className="Electrophoresis-item form-group">
                    <label>
                      A : {prenupData.HBELECA || '0'} %
                    </label>
                    <div>
                      <div>
                        <label className="form-check-label">
                          <b>{prenupData.HBELECASTAT || 'Normal'}</b>
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="Electrophoresis-item form-group">
                    <label>
                      F : {prenupData.HBELECF || '0'} %
                    </label>
                    <div>
                      <div>
                        <label className="form-check-label">
                          <b>{prenupData.HBELECFSTAT || 'Normal'}</b>
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="Electrophoresis-item form-group">
                    <label>
                      A2 : {prenupData.HBELECA2 || '0'} %
                    </label>
                    <div>
                      <div>
                        <label className="form-check-label">
                          <b>{prenupData.HBELECA2STAT || 'Normal'}</b>
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="Electrophoresis-item form-group">
                    <label>
                      C : {prenupData.HBELECC || '0'} %
                    </label>
                    <div>
                      <div>
                        <label className="form-check-label">
                          <b>{prenupData.HBELECCSTAT || 'Normal'}</b>
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="Electrophoresis-item form-group">
                    <label>
                      S : {prenupData.HBELECS || '0'} %
                    </label>
                    <div>
                      <div>
                        <label className="form-check-label">
                          <b>{prenupData.HBELECSSTAT || 'Normal'}</b>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-12">
              <h2>إقرار المنتفع/المنتفعة بإعلامه بنتيجة الفحص وتوصيات الطبيب</h2>

              <div style={{backgroundColor:'white'}}>
                <div className="row pt-2">
                  <div className="col-4 pt-4">
                    <p className="mr-3 font14">اسم الممرض/الممرضة : --------------</p>
                    <p className="mr-3 font14">اسم الطبيب/الطبيبة : -----------------</p>
                    <p className="mr-3 font14">مدير الوحدة : -------------------------</p>
                  </div>
                  <div className="col-4 pt-4">
                    <p className="mr-3 font14">التوقيع : ----------------------</p>
                    <p className="mr-3 font14">التوقيع : ----------------------</p>
                    <p className="mr-3 font14">التوقيع : ----------------------</p>
                  </div>
                  <div className="col-3">
                    <canvas ref={circleRef1} className="circle-stamp" width="115" height="115"></canvas>
                    <p style={{paddingTop:'2px', paddingRight:'45px'}}>ختم شعار الجمهورية</p>
                  </div>
                </div>

                <div className="row pt-2">
                  <div className="col-6">
                    <p className="mr-3 font14" style={{fontFamily: 'cairo'}}>أقر أنا الموقع/الموقعه أدناه : <span className="font-weight-bold">{userData?.fullName || '-'}</span></p>
                  </div>
                  <div className="col-4">
                    <p className="mr-3 font14" style={{fontFamily: 'cairo'}}>{prenupData.nationalityType === 'N' ? 'رقم جواز السفر' : 'رقم القومى'} : <span className="font-weight-bold">{userData?.nationalId || '-'}</span></p>
                  </div>
                </div>

                <p className="mr-3 font14" style={{fontFamily: 'cairo'}}>
                  بأنه قد تم إعلامى بنتيجة الفحص الطبى والتوصيات الطبية المذكورة سابقا وقد تلقيت المشورة الخاصة بحالتى الصحية وألتزم بإعلام طرف الزواج الأخر
                  قبل إجراءات الزواج وأصبحت بذلك مسئول عما يترتب على ذلك دون أدنى مسئولية على المنشأة الصحية والفريق الطبى الذى يمثلها .
                </p><br/>

                <div className="row">
                  <div className="col-4 border-left">
                    <p className="mr-3 font14">الاسم (رباعى) : ------------------</p>
                    <p className="mr-3 font14">التوقيع : -----------------------</p>
                  </div>
                  <div className="col-3 border-left">
                    <canvas ref={circleRef2} className="circle-stamp" width="115" height="115"></canvas>
                    <p style={{paddingTop:'2px', textAlign:'center'}}>بصمة الإبهام</p>
                  </div>
                  <div className="col-5">
                    <p className="mr-3 font14">اسم الطرف الاخر(رباعى) : ---------------</p>
                    <p className="mr-3 font14">توقيع الطرف الاخر : --------------------</p>
                    <p className="mr-3 font14">{prenupData.partnerNationalityType === 'N' ? 'رقم جواز سفر الطرف الاخر' : 'الرقم القومى للطرف الاخر'} : -------------</p>
                  </div>
                </div>

                <div className="row">
                  <div className="col-sm-5 pt-3"><p className="mr-3 font14" style={{color:'red', fontSize:'14px'}}>*هذه الوثيقة صالحة لمدة ستة اشهر من تاريخ الإصدار</p></div>
                  <div className="col-sm-3"></div>
                  <div className="col-sm-4">
                    {qrCodeUrl ? (
                      <canvas
                        ref={qrCanvasRef}
                        className="qr-code img-thumbnail img-responsive"
                        width="100"
                        height="110"
                      />
                    ) : (
                      <div style={{width: '100px', height: '110px', border: '1px solid #ddd', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        QR Code
                      </div>
                    )}
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-8"></div>
                  <div className="col-sm-4 mb-1"><p style={{color:'black', fontWeight:'bold', fontSize:'14px'}}>{prenupData.documentNumber || ''}</p></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <br />

      <div className="text-center">
        {user && (
          <button
            onClick={handlePrint}
            className="btn btn-success btn-md mx-2"
            id="printBtn"
          >
            <>طباعة</>
          </button>
        )}

        <a
          href="javascript:window.close();"
          className="btn btn-danger btn-md"
          id="closeBtn"
        >
          <>إغلاق</>
        </a>
      </div>

      <PrintFooter />
    </div>
  );
};

export default PrintSheet;
